import {
  ChangeDetectorRef,
  Component,
  OnInit,
  AfterViewChecked,
  ViewChild,
  ElementRef,
  inject,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { EVENT_TYPES, WEATHER_ICONS } from 'src/app/models/event.model';
import * as L from 'leaflet';
import { SaasGainCardComponent } from 'src/app/components/shared/saas-gain-card/saas-gain-card.component';

export interface EventRapport {
  event: {
    id: string;
    name: string;
    eventDate: string;
    eventType: string;
    location: string;
    locationLabel: string;
    locationAddress: string;
    locationCoordinates: { lat: number; lng: number } | null;
    locationRouteData: any | null;
    startTime: string;
    endTime: string;
    duration: number | null;
    expectedAttendance: number;
    weatherCondition: string | null;
    temperature: number | null;
    notes: string | null;
    status: string;
  };
  salesIds: string[];
  kpis: {
    totalRevenue: number;
    totalWaste: number;
    netProfit: number;
    netRate: number;
    avgAccuracy: number | null;
    totalRuptures: number;
    estimatedRevenue: number;
    revenueVsEstimate: number | null;
    wasteRate: number;
    score: number;
    affluence: string | null;
    salesCount: number;
    totalRealProfit: number | null;
    realProfitRate: number | null;
  };
  products: {
    name: string;
    prepared: number;
    sold: number;
    wasted: number;
    recommended: number;
    revenue: number;
    wasteAmount: number;
    hadRupture: boolean;
    sellRate: number;
    accuracy: number | null;
    unitPrice: number;
    purchasePrice: number;
    wasteValueSaved: number;
    missedSales: number;
    missedRevenue: number;
    realCost: number | null;
    realProfit: number | null;
    perishability: 'high' | 'medium' | 'low';
  }[];
  starProduct: any;
  notes: string[];
}

@Component({
  selector: 'app-event-report',
  standalone: true,
  imports: [CommonModule, RouterModule, SaasGainCardComponent],
  templateUrl: './event-report.component.html',
  styles: [
    `
      .route-map-container {
        height: 280px;
        width: 100%;
        display: block;
      }
      :host ::ng-deep .route-map-container .leaflet-container {
        height: 100%;
        width: 100%;
        border-radius: 0;
      }
    `,
  ],
})
export class EventReportComponent implements OnInit, AfterViewChecked {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  rapport: EventRapport | null = null;
  loading = true;
  error = '';
  private apiUrl = `${environment.apiUrl}/events`;
  weatherIcons = WEATHER_ICONS;

  @ViewChild('routeMapContainer', { static: false })
  routeMapContainer?: ElementRef;
  private routeMap?: L.Map;
  private routeMapInitialized = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
  ) {
    this.destroyRef.onDestroy(() => {
      if (this.routeMap) {
        this.routeMap.remove();
        this.routeMap = undefined;
      }
    });
  }

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error = 'ID manquant';
      this.loading = false;
      return;
    }
    this.http
      .get<EventRapport>(`${this.apiUrl}/${id}/rapport`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.rapport = data;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = 'Impossible de charger le rapport.';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  ngAfterViewChecked(): void {
    if (
      !this.routeMapInitialized &&
      !this.loading &&
      this.rapport?.event.locationCoordinates &&
      this.routeMapContainer?.nativeElement
    ) {
      this.routeMapInitialized = true;
      setTimeout(() => this.initRouteMap(), 0);
    }
  }

  private initRouteMap(): void {
    const coords = this.rapport?.event.locationCoordinates;
    if (!coords || !this.routeMapContainer?.nativeElement || this.routeMap)
      return;

    this.routeMap = L.map(this.routeMapContainer.nativeElement, {
      scrollWheelZoom: false,
      doubleClickZoom: false,
      zoomControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(this.routeMap);

    const routeData = this.rapport?.event.locationRouteData;
    if (routeData?.geometry?.coordinates?.length) {
      L.geoJSON(routeData.geometry as any, {
        style: { color: '#3b82f6', weight: 5, opacity: 0.75 },
      }).addTo(this.routeMap);
      const first = routeData.geometry.coordinates[0];
      L.marker([first[1], first[0]], {
        draggable: false,
        icon: L.divIcon({
          html: '<span style="font-size:22px;line-height:1;">&#x1F3E0;</span>',
          className: '',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        }),
      }).addTo(this.routeMap);
      L.marker([coords.lat, coords.lng], {
        draggable: false,
        icon: L.divIcon({
          html: '<span style="font-size:22px;line-height:1;">&#x1F535;</span>',
          className: '',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        }),
      }).addTo(this.routeMap);
      const latlngs = routeData.geometry.coordinates.map(
        (c: number[]) => [c[1], c[0]] as L.LatLngTuple,
      );
      this.routeMap.fitBounds(L.latLngBounds(latlngs), { padding: [30, 30] });
    } else {
      this.routeMap.setView([coords.lat, coords.lng], 13);
      L.marker([coords.lat, coords.lng]).addTo(this.routeMap);
    }
    setTimeout(() => this.routeMap?.invalidateSize(), 100);
  }

  getWeatherIcon(condition?: string | null): string {
    if (!condition) return '☁️';
    return this.weatherIcons[condition as keyof typeof WEATHER_ICONS] || '☁️';
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m.toString().padStart(2, '0')}min` : `${h}h`;
  }

  getEventIcon(type: string): string {
    return EVENT_TYPES.find((t) => t.value === type)?.icon ?? '📍';
  }

  get scoreColor(): string {
    const s = this.rapport?.kpis.score ?? 0;
    if (s >= 80) return 'text-green-600';
    if (s >= 60) return 'text-yellow-600';
    return 'text-red-600';
  }

  get scoreBg(): string {
    const s = this.rapport?.kpis.score ?? 0;
    if (s >= 80) return 'bg-green-50 border-green-200';
    if (s >= 60) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  }

  get scoreLabel(): string {
    const s = this.rapport?.kpis.score ?? 0;
    if (s >= 85) return 'Excellent';
    if (s >= 70) return 'Bon';
    if (s >= 55) return 'Correct';
    return 'À améliorer';
  }

  readonly industryWasteRate = 15;
  readonly industryMissedRate = 7;

  get totalPrepared(): number {
    return (
      this.rapport?.products.reduce((s, p) => s + (p.prepared || 0), 0) ?? 0
    );
  }
  get totalSold(): number {
    return this.rapport?.products.reduce((s, p) => s + (p.sold || 0), 0) ?? 0;
  }
  get totalWasted(): number {
    return this.rapport?.products.reduce((s, p) => s + (p.wasted || 0), 0) ?? 0;
  }

  // Charges fixes estimées : ~15% du CA effectif (aligné avec SaasGainCard)
  readonly fixedCostsRate = 15;

  /** Coût marchand réel (si disponible) */
  get totalRealCostKpi(): number | null {
    const k = this.rapport?.kpis;
    if (!k) return null;
    return k.totalRealProfit !== null
      ? k.totalRevenue - k.totalRealProfit
      : null;
  }

  /** Base pour gaspillage estimé SANS outil = coût marchand réel × 15% */
  get estimatedWasteWithoutKpi(): number {
    const k = this.rapport?.kpis;
    if (!k) return 0;
    const base =
      this.totalRealCostKpi ??
      Math.round(k.totalRevenue * (1 - (k.netRate ?? 38) / 100));
    return Math.round((base * this.industryWasteRate) / 100);
  }

  get wasteReduction(): number {
    if (!this.rapport) return 0;
    return Math.max(
      0,
      this.estimatedWasteWithoutKpi - this.rapport.kpis.totalWaste,
    );
  }

  get estimatedMissedRevenueKpi(): number {
    return Math.round(
      ((this.rapport?.kpis.totalRevenue ?? 0) * this.industryMissedRate) / 100,
    );
  }

  get actualMissedRevenueKpi(): number {
    const plafond = this.estimatedMissedRevenueKpi;
    const products = this.rapport?.products.length ?? 0;
    const ruptures = this.rapport?.kpis.totalRuptures ?? 0;
    if (products <= 0) return plafond;
    return Math.round(plafond * Math.min(1, ruptures / products));
  }

  get deltaMissedRevenueKpi(): number {
    return Math.max(
      0,
      this.estimatedMissedRevenueKpi - this.actualMissedRevenueKpi,
    );
  }

  /** CA effectif AVEC outil = CA - coût - gaspi (aligné avec SaasGainCard) */
  get caEffectifAvecKpi(): number {
    const k = this.rapport?.kpis;
    if (!k) return 0;
    const cost = this.totalRealCostKpi ?? 0;
    return Math.max(0, k.totalRevenue - cost - k.totalWaste);
  }

  /** CA effectif SANS outil = CA - coût - gaspiEstimé */
  get caEffectifSansKpi(): number {
    const k = this.rapport?.kpis;
    if (!k) return 0;
    const cost =
      this.totalRealCostKpi ??
      Math.round(k.totalRevenue * (1 - (k.netRate ?? 38) / 100));
    return Math.max(0, k.totalRevenue - cost - this.estimatedWasteWithoutKpi);
  }

  get netProfitAfterFixed(): number {
    const fixed = Math.round(
      (this.caEffectifAvecKpi * this.fixedCostsRate) / 100,
    );
    return Math.max(0, this.caEffectifAvecKpi - fixed);
  }

  get deltaNetProfit(): number {
    const fixedSans = Math.round(
      (this.caEffectifSansKpi * this.fixedCostsRate) / 100,
    );
    const netSans = Math.max(0, this.caEffectifSansKpi - fixedSans);
    return Math.max(0, this.netProfitAfterFixed - netSans);
  }

  sellRateColor(rate: number): string {
    if (rate >= 85) return 'bg-green-500';
    if (rate >= 65) return 'bg-yellow-400';
    return 'bg-red-400';
  }

  accuracyColor(acc: number | null): string {
    if (acc == null) return 'text-gray-400';
    if (acc >= 85) return 'text-green-600';
    if (acc >= 65) return 'text-yellow-600';
    return 'text-red-600';
  }

  revenueVsEstimateLabel(val: number | null): string {
    if (val == null) return '—';
    if (val >= 100) return `+${val - 100}% vs prévu`;
    return `${val - 100}% vs prévu`;
  }

  revenueVsEstimateClass(val: number | null): string {
    if (val == null) return 'text-gray-400';
    return val >= 100 ? 'text-green-600' : 'text-orange-500';
  }

  goBack(): void {
    this.router.navigate(['/events']);
  }

  editSales(): void {
    if (!this.rapport) return;
    const params: any = { eventId: this.rapport.event.id };
    if (this.rapport.salesIds?.length === 1) {
      params['editSaleId'] = this.rapport.salesIds[0];
    }
    this.router.navigate(['/sales/entry-itinerant'], { queryParams: params });
  }

  min(a: number, b: number): number {
    return Math.min(a, b);
  }
  max(a: number, b: number): number {
    return Math.max(a, b);
  }
}
